import { useState } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import {
  AdminSourceResponse,
  RootStatus,
  UrlStatus,
} from "@authory-packages/core";
import { FieldSetStyles } from "../../styles";

/**
 * The stages SourceManager groups under its "In progress" and "Import finished"
 * headings. Outside these the bar is either empty or static, so it earns no room.
 *
 * Deliberately wider than SourceDetails' own isInProgress, which omits
 * SearchEngineFullCrawlRunning despite SourceManager filing it under "In progress".
 */
const VISIBLE_STAGES = [
  RootStatus.FullCrawlRunning,
  RootStatus.FullCrawlAnalyzeRunning,
  RootStatus.SearchEngineFullCrawlRunning,
  RootStatus.ImportFinished,
];

export const showCrawlProgressFor = (stage: RootStatus) =>
  VISIBLE_STAGES.includes(stage);

type SourceCrawlProgressProps = {
  source: AdminSourceResponse;
  onRefresh: () => Promise<void>;
};

type Segment = {
  label: string;
  color: string;
  statuses: UrlStatus[];
  /**
   * Rendered hatched with a dashed outline. Core marks these as either going out
   * of usage with the pipeline rework (FullCrawlAutoTune, ReCheck*) or fully
   * deprecated (StaticScoreCompute, StaticCalibrate, Diffbot*, Shiva). They only
   * surface on sources crawled before the rework, and only when non-zero.
   */
  deprecated?: boolean;
};

/**
 * Live pipeline stages in the order a URL travels through them, then the
 * deprecated stages, then the terminal verdicts. Colors follow the stage family,
 * so a deprecated stage reads as a dashed variant of the stage it belonged to.
 */
const SEGMENTS: Segment[] = [
  {
    label: "Static/Early Reject",
    color: "#1546e0",
    statuses: [UrlStatus.StaticAnalyze, UrlStatus.EarlyRejectCheck],
  },
  {
    label: "Dyna",
    color: "#f7c6c6",
    statuses: [UrlStatus.DynamicAnalyze],
  },
  {
    label: "LLM",
    color: "#7b3fb5",
    statuses: [UrlStatus.LLMAnalyze],
  },
  {
    label: "Check",
    color: "#00838f",
    statuses: [UrlStatus.DynamicAnalyzeCheck],
  },
  {
    label: "Acc",
    color: "#34a853",
    statuses: [UrlStatus.Accepted],
  },
  {
    label: "Rej",
    color: "#800000",
    statuses: [UrlStatus.Rejected],
  },
  {
    label: "Err",
    color: "#d93025",
    statuses: [UrlStatus.PermanentError],
  },
   // All of these below are Deprecated
  {
    label: "Tune",
    color: "#1546e0",
    statuses: [UrlStatus.FullCrawlAutoTune],
    deprecated: true,
  },
  {
    label: "Stat (old)",
    color: "#1546e0",
    statuses: [UrlStatus.StaticScoreCompute, UrlStatus.StaticCalibrate],
    deprecated: true,
  },
  {
    label: "Diffbot",
    color: "#f7c6c6",
    statuses: [UrlStatus.DiffbotAnalyze, UrlStatus.DiffbotAnalyzeCheck],
    deprecated: true,
  },
  {
    label: "Shiva",
    color: "#f7c6c6",
    statuses: [UrlStatus.ShivaContentExtraction],
    deprecated: true,
  },
  {
    label: "ReChk",
    color: "#34a853",
    statuses: [UrlStatus.ReCheckSuccess],
    deprecated: true,
  },
  {
    label: "ReChk err",
    color: "#d93025",
    statuses: [UrlStatus.ReCheckFailed],
    deprecated: true,
  },
];

const REMAINDER_COLOR = "#e0e0e0";

const BAR_HEIGHT = 6;

/**
 * Width of the refresh IconButton (24px icon + 3px padding each side). The bar
 * shares a row with it, so the label row is inset by the same amount to stay
 * aligned under the bar.
 */
const REFRESH_WIDTH = 30;

const hatched = (color: string) =>
  `repeating-linear-gradient(45deg, ${color}, ${color} 2px, #fff 2px, #fff 5px)`;

const useStyles = makeStyles({
  bar: {
    display: "flex",
    height: BAR_HEIGHT,
    borderRadius: 2,
    overflow: "hidden",
  },
  segment: {
    boxSizing: "border-box",
    borderRight: "1px solid #fff",
  },
  labels: {
    display: "flex",
    paddingRight: REFRESH_WIDTH,
  },
});

export const SourceCrawlProgress = ({
  source,
  onRefresh,
}: SourceCrawlProgressProps) => {
  const fieldSetStyles = FieldSetStyles();
  const classes = useStyles();
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  const counts = SEGMENTS.map((segment) =>
    segment.statuses.reduce(
      (sum, status) => sum + (source.countsPerStatus[status] || 0),
      0,
    ),
  );

  // urlCount is the denominator the rest of the panel already uses, so the bar
  // agrees with SourceStats/SourceDiagnostics rather than inventing its own total.
  const total = source.urlCount;
  // Whatever the segments do not claim — chiefly URLs still in Processing.
  const other = Math.max(0, total - counts.reduce((sum, c) => sum + c, 0));

  const parts = [
    ...SEGMENTS.map((segment, index) => ({
      ...segment,
      count: counts[index],
    })),
    { label: "Other", color: REMAINDER_COLOR, count: other, deprecated: false },
  ].filter((part) => part.count > 0);

  const percent = (count: number) => (count / total) * 100;

  return (
    <fieldset className={fieldSetStyles.container}>
      <legend>Crawl progress:</legend>
      <Box pl={1}>
        {/* Bar and button share a row, so the button centres on the bar. */}
        <Box display="flex" alignItems="center">
          <Box flexGrow={1}>
            <Box className={classes.bar}>
              {total === 0 ? (
                <Box width="100%" bgcolor={REMAINDER_COLOR} />
              ) : (
                parts.map((part) => (
                  <Tooltip
                    key={part.label}
                    title={`${part.label}: ${part.count} (${Math.round(
                      percent(part.count),
                    )}%)${part.deprecated ? " - deprecated" : ""}`}
                  >
                    <Box
                      className={classes.segment}
                      width={`${percent(part.count)}%`}
                      style={{
                        background: part.deprecated
                          ? hatched(part.color)
                          : part.color,
                        border: part.deprecated
                          ? `1px dashed ${part.color}`
                          : undefined,
                      }}
                    />
                  </Tooltip>
                ))
              )}
            </Box>
          </Box>
          <Tooltip title="Refresh this source">
            <span>
              <IconButton
                size="small"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                {refreshing ? <CircularProgress size={18} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Box className={classes.labels}>
          {total === 0
            ? null
            : parts.map((part) => (
                <Box
                  key={part.label}
                  width={`${percent(part.count)}%`}
                  textAlign="center"
                  overflow="hidden"
                >
                  <Typography variant="caption" color="textSecondary" noWrap>
                    {`${part.label}: ${part.count} (${Math.round(
                      percent(part.count),
                    )}%)`}
                  </Typography>
                </Box>
              ))}
        </Box>
      </Box>
    </fieldset>
  );
};
